import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MapleLeaf = ({ className, style }) => (
  <svg viewBox="0 0 100 100" className={className} style={style} fill="currentColor">
    <path d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
  </svg>
);

const fallingLeaves = [
  { id: 1, size: 52, left: '70%', delay: 0, duration: 14, color: '#1a5c1e' },
  { id: 2, size: 42, left: '80%', delay: 2, duration: 16, color: '#a6533a' },
  { id: 3, size: 48, left: '60%', delay: 4, duration: 13, color: '#2d5a2d' },
  { id: 4, size: 38, left: '85%', delay: 1, duration: 17, color: '#8b6b4a' },
  { id: 5, size: 56, left: '55%', delay: 3, duration: 15, color: '#1a5c1e' },
  { id: 6, size: 36, left: '90%', delay: 5, duration: 18, color: '#a6533a' },
  { id: 7, size: 44, left: '75%', delay: 6, duration: 14, color: '#6b5344' },
  { id: 8, size: 40, left: '65%', delay: 7, duration: 16, color: '#3d5a3d' },
];

const servicesData = [
  {
    num: '01',
    name: 'ESG Advisory',
    quote: 'The foundation of everything credible in sustainability.',
    text: 'Sustainability only creates value when it is structured, measurable, and embedded into how an organization actually operates.',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
    items: [
      'ESG risk screening across environmental, social, and governance dimensions',
      'Double materiality assessment and risk and opportunity mapping',
      'Gap analysis against CSRD, ISSB, GRI, TCFD, SASB, CDP, and UNGC',
      'Phased ESG strategy and roadmap with clear milestones',
      'ESG Action Plans with monitoring and progress frameworks',
      'Management systems, policies, and internal controls',
      'Disclosure architecture and reporting frameworks'
    ]
  },
  {
    num: '02',
    name: 'Carbon Market Advisory',
    quote: 'From project idea to issued credit.',
    text: 'Carbon markets are complex and unforgiving of shortcuts. The work is structured around methodological integrity and long-term credibility.',
    image: 'https://images.unsplash.com/photo-1562696673-3a95c6045a5a?w=800&q=80',
    items: [
      'Carbon project design and methodology selection',
      'MRV framework development',
      'Credit origination and market strategy',
      'Compliance market and Article 6 navigation',
      'Registry and verification support'
    ]
  },
  {
    num: '03',
    name: 'CBAM Advisory',
    quote: 'Get ahead before it becomes a liability.',
    text: 'CBAM is reshaping trade flows and supplier relationships. Early action creates transparency that compounds.',
    image: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=800&q=80',
    items: [
      'CBAM exposure assessment and product mapping',
      'Embedded carbon calculation at supplier level',
      'Supplier data collection and engagement frameworks',
      'Compliance declarations and documentation',
      'Strategic decarbonization to reduce CBAM liability'
    ]
  },
  {
    num: '04',
    name: 'Climate Finance Advisory',
    quote: 'Capital that reaches the projects that need it.',
    text: 'The gap between climate ambition and investable reality is about structure, credibility, and finding the right capital.',
    image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80',
    items: [
      'Blended finance structuring and fund design',
      'Project bankability assessment',
      'Investor-ready documentation and data rooms',
      'DFI, grant, and MDB navigation',
      'Impact measurement and reporting frameworks'
    ]
  },
  {
    num: '05',
    name: 'Sustainable Supply Chain',
    quote: 'Resilience and responsibility, built into how you operate.',
    text: 'Supply chains are where most environmental and social risk actually lives and where most sustainability strategies fall short.',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80',
    items: [
      'Scope 3 emissions mapping and hotspot analysis',
      'Supplier ESG risk profiling',
      'Supplier engagement and capacity building',
      'Science-aligned decarbonization pathways',
      'Supply chain disclosure support'
    ]
  },
  {
    num: '06',
    name: 'GHG Accounting',
    quote: 'The baseline every credible climate commitment starts from.',
    text: 'You cannot manage what you have not measured. Inventories built to withstand third-party verification.',
    image: 'https://images.unsplash.com/photo-1707852710487-8f7837975208?w=800&q=80',
    items: [
      'Scope 1, 2, and 3 inventory development',
      'Data collection framework design',
      'Boundary and consolidation approach',
      'SBTi alignment and target-setting support',
      'Verification-ready documentation'
    ]
  }
];

const ConsultPage = () => {
  return (
    <main data-testid="consult-page" className="overflow-hidden">
      {/* Hero Section - Maple Light Theme */}
      <section className="relative min-h-[55vh] flex items-center pt-24 pb-10 overflow-hidden">
        {/* Background with nature image + white overlays */}
        <div className="absolute inset-0 -top-20">
          <img 
            src="https://images.unsplash.com/photo-1601655856683-8400aa9ef59f?w=1920&q=80"
            alt="Abstract green and brown canvas"
            className="w-full h-full object-cover"
            style={{ filter: 'saturate(0.6) brightness(1.1) contrast(0.95) sepia(0.15)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white" />
          <div className="absolute inset-0 bg-gradient-to-tr from-brand-green/8 via-transparent to-brand-brown/12" />
        </div>
        
        {/* Floating Orbs */}
        <motion.div 
          animate={{ y: [-8, 8, -8] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute right-[15%] top-[20%] w-20 h-20 rounded-full bg-gradient-to-br from-brand-green/20 to-brand-green/5 blur-xl hidden lg:block"
        />
        <motion.div 
          animate={{ y: [8, -8, 8] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute right-[25%] bottom-[25%] w-32 h-32 rounded-full bg-gradient-to-br from-brand-brown/15 to-brand-brown/5 blur-xl hidden lg:block"
        />
        
        {/* Falling Maple Leaves */}
        <div className="absolute inset-0 z-[2] overflow-hidden pointer-events-none">
          {fallingLeaves.map((leaf) => (
            <motion.div
              key={leaf.id}
              className="absolute"
              style={{ left: leaf.left, top: -50 }}
              initial={{ y: -50, rotate: 0, opacity: 0 }}
              animate={{ 
                y: ['0vh', '110vh'],
                rotate: [0, 360, 180, 360],
                opacity: [0, 0.45, 0.45, 0]
              }}
              transition={{
                duration: leaf.duration,
                delay: leaf.delay,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <motion.div
                animate={{ 
                  x: [0, -30, 15, -20, 0],
                  rotateY: [0, 180, 360]
                }}
                transition={{
                  duration: leaf.duration / 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <MapleLeaf 
                  className="drop-shadow-sm"
                  style={{ width: leaf.size, height: leaf.size, color: leaf.color }} 
                />
              </motion.div>
            </motion.div>
          ))}
        </div>
        
        {/* Content */}
        <div className="w-full pl-6 md:pl-12 lg:pl-24 pr-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="max-w-2xl"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-brand-green/20 mb-6"
            >
              <Sparkles className="w-4 h-4 text-brand-green" />
              <span className="font-sans text-sm font-semibold text-brand-green uppercase tracking-wider">Our Services</span>
            </motion.div>
            
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-dark mb-5 leading-[1.1]">
              Consultation <span className="bg-gradient-to-r from-brand-brown to-brand-green bg-clip-text text-transparent">Services</span>
            </h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="font-sans text-lg md:text-xl text-gray-700 leading-relaxed max-w-xl mb-8"
            >
              The full sustainability stack, from ESG foundations to carbon markets and climate finance.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/contact">
                <Button className="btn-gradient text-white font-sans font-medium px-6 py-5 text-sm rounded-full shadow-lg">
                  Start Your Journey <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="mt-10 flex items-center gap-4"
            >
              <div className="h-px w-12 bg-gradient-to-r from-brand-green to-brand-brown" />
              <span className="font-sans text-sm text-gray-500 italic">Structured sustainability, measurable impact</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Quick Nav */}
      <section className="py-4 bg-white border-y border-gray-100 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center gap-2 md:gap-3">
            {servicesData.map((s) => (
              <button
                key={s.num}
                onClick={() => document.getElementById('svc-' + s.num)?.scrollIntoView({ behavior: 'smooth' })}
                className="group flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 hover:border-brand-green hover:bg-brand-green/5 transition-all duration-300"
              >
                <span className="font-mono text-xs text-brand-brown group-hover:text-brand-green transition-colors">{s.num}</span>
                <span className="font-sans text-sm text-gray-600 group-hover:text-brand-green transition-colors">{s.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="font-mono text-xs text-brand-brown uppercase tracking-widest">What We Do</span>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mt-3">
              Six Pillars of <span className="italic bg-gradient-to-r from-brand-green to-brand-brown bg-clip-text text-transparent">Impact</span>
            </h2>
          </motion.div>
          
          {/* Services Grid */}
          <div className="space-y-14">
            {servicesData.map((service, index) => {
              const isEven = index % 2 === 0;
              return (
                <motion.div
                  key={service.num}
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.7 }}
                  id={'svc-' + service.num}
                  className="relative"
                >
                  <div className={'grid lg:grid-cols-2 gap-8 lg:gap-12 items-center'}>
                    {/* Image Side */}
                    <motion.div
                      initial={{ opacity: 0, x: isEven ? -40 : 40 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                      className={'relative ' + (!isEven ? 'lg:order-2' : '')}
                    >
                      <div className="relative group">
                        {/* Glow Effect */}
                        <div className="absolute -inset-3 bg-gradient-to-br from-brand-green/20 to-brand-brown/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        
                        {/* Image Container */}
                        <div className="relative overflow-hidden rounded-2xl shadow-xl">
                          <img 
                            src={service.image} 
                            alt={service.name}
                            className="w-full h-60 md:h-72 object-cover group-hover:scale-105 transition-transform duration-700"
                          />
                          
                          {/* Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 via-transparent to-transparent" />
                          
                          {/* Number Badge */}
                          <div className="absolute top-5 left-5">
                            <motion.div 
                              whileHover={{ scale: 1.1, rotate: 5 }}
                              className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-green to-brand-brown flex items-center justify-center shadow-lg"
                            >
                              <span className="font-mono text-xl font-bold text-white">{service.num}</span>
                            </motion.div>
                          </div>
                          
                          {/* Tagline */}
                          <div className="absolute bottom-5 left-5 right-5">
                            <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                              <p className="font-serif text-base text-brand-dark italic">{service.quote}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                    
                    {/* Content Side */}
                    <motion.div
                      initial={{ opacity: 0, x: isEven ? 40 : -40 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.3 }}
                      className={!isEven ? 'lg:order-1' : ''}
                    >
                      <h3 className="font-serif text-2xl md:text-3xl text-brand-dark mb-3">
                        {service.name}
                      </h3>
                      
                      <p className="font-sans text-gray-600 leading-relaxed mb-4">
                        {service.text}
                      </p>
                      
                      <div className="space-y-2 mb-6">
                        {service.items.map((item, i) => (
                          <motion.div 
                            key={i}
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.4 + i * 0.05 }}
                            className="flex items-start gap-3 group/item"
                          >
                            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-brand-green to-brand-brown mt-2 flex-shrink-0 group-hover/item:scale-150 transition-transform" />
                            <span className="font-sans text-sm text-gray-500 group-hover/item:text-gray-700 transition-colors">{item}</span>
                          </motion.div>
                        ))}
                      </div>
                      
                      <Link to="/contact">
                        <Button className="group bg-brand-surface hover:bg-brand-green/10 text-brand-dark border border-gray-200 hover:border-brand-green rounded-full px-6 py-5 transition-all">
                          Get Started <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform text-brand-green" />
                        </Button>
                      </Link>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section - Light Theme */}
      <section className="py-14 relative overflow-hidden bg-gradient-to-br from-brand-surface via-white to-brand-surface">
        {/* Decorative Elements */}
        <motion.div 
          animate={{ scale: [1, 1.1, 1], opacity: [0.1, 0.2, 0.1] }}
          transition={{ duration: 6, repeat: Infinity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand-green/10 rounded-full blur-[100px]"
        />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-dark leading-tight mb-4">
              Ready to move from{' '}
              <span className="italic text-brand-green">ambition</span>
              {' '}to{' '}
              <span className="italic text-brand-brown">action</span>?
            </h2>
            
            <p className="font-sans text-base text-gray-600 mb-8 max-w-2xl mx-auto">
              Let us discuss how we can structure sustainability into your operations and turn it into measurable value.
            </p>
            
            <Link to="/contact">
              <Button className="group bg-gradient-to-r from-brand-green to-emerald-500 text-white px-8 py-6 rounded-full text-base font-semibold shadow-xl shadow-brand-green/20 hover:shadow-brand-green/40 transition-all">
                Start the Conversation <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-2 transition-transform" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default ConsultPage;

